/** Browser @page size tokens (Portrait). See MDN `size`. */
export type PrintPaperId = 'letter' | 'a4' | 'legal' | 'tabloid';

/** `@page` margins in mm (top / right / bottom / left). Extra bottom reduces clash with browser UI; browsers cannot suppress URL/date via CSS. */
export interface PrintPageMarginsMm {
  top: number;
  right: number;
  bottom: number;
  left: number;
}

/** Generous inset (~1.5 in top, ~1.2 in sides, extra bottom for browser footer bands). Tune via PrintChrome `pageMarginsMm` if needed. */
export const DEFAULT_PRINT_PAGE_MARGINS_MM: PrintPageMarginsMm = {
  top: 38,
  right: 30,
  bottom: 42,
  left: 30,
};

export function mergePrintPageMargins(partial?: Partial<PrintPageMarginsMm>): PrintPageMarginsMm {
  return { ...DEFAULT_PRINT_PAGE_MARGINS_MM, ...partial };
}

export function formatPrintPageMarginsCss(m: PrintPageMarginsMm): string {
  return `${m.top}mm ${m.right}mm ${m.bottom}mm ${m.left}mm`;
}

/** Printable width inside `@page` margins (optional caller tuning). */
export function printContentWidthMm(pageWidthMm: number, m: PrintPageMarginsMm): number {
  return Math.max(48, pageWidthMm - m.left - m.right);
}

export interface PrintPaperPreset {
  id: PrintPaperId;
  label: string;
  /** Value for CSS `@page { size: … }` */
  pageSizeCss: string;
  /** Approximate preview max-width for screen (Tailwind-less string for inline style optional) */
  previewMaxWidthMm: number;
}

export const PRINT_PAPER_PRESETS: PrintPaperPreset[] = [
  { id: 'letter', label: 'US Letter (8.5×11")', pageSizeCss: 'letter', previewMaxWidthMm: 216 },
  { id: 'a4', label: 'A4', pageSizeCss: 'A4', previewMaxWidthMm: 210 },
  { id: 'legal', label: 'US Legal (8.5×14")', pageSizeCss: 'legal', previewMaxWidthMm: 216 },
  { id: 'tabloid', label: 'Tabloid (11×17")', pageSizeCss: 'tabloid', previewMaxWidthMm: 279 },
];

export function getPrintPaperPreset(id: PrintPaperId): PrintPaperPreset {
  return PRINT_PAPER_PRESETS.find((p) => p.id === id) ?? PRINT_PAPER_PRESETS[1];
}

export const STORAGE_KEY_PRINT_PAPER = 'priorities-ai:print-default-paper';

export function loadSavedPrintPaperId(): PrintPaperId {
  try {
    const raw = localStorage.getItem(STORAGE_KEY_PRINT_PAPER);
    if (raw && PRINT_PAPER_PRESETS.some((p) => p.id === raw)) return raw as PrintPaperId;
  } catch {
    /* ignore */
  }
  return 'letter';
}

export function savePrintPaperId(id: PrintPaperId): void {
  try {
    localStorage.setItem(STORAGE_KEY_PRINT_PAPER, id);
  } catch {
    /* ignore */
  }
}
